// ============================================
// AGREGARACOLECCIONMODAL.JS — Agregar un producto a una o varias colecciones
// ============================================
// Se abre desde La Tienda (boton "Coleccion" de cada tarjeta). Permite elegir
// a que coleccion(es) agregar el producto, o crear una nueva al vuelo. Replica
// el patron de AgregarAListaModal pero contra /api/colecciones.
//
// Las colecciones NO tienen cliente ni precio pactado: el producto se agrega a
// precio NORMAL (se calcula al vuelo). El congelado se hace despues, por item,
// desde la pantalla de Colecciones.

import React, { useEffect, useState } from 'react';
import * as LucideIcons from 'lucide-react';
import { fetchAuth } from '../utils/sesion';
import { IconoLucide } from './SelectorIconoLucide';
import { API_BASE_CLI } from './helpersClientes';

const PP = "'Poppins', sans-serif";
const colores = {
  principal: '#5c9adb', oscuro: '#2f5f9e', gris: '#4e5357', grisClaro: '#5e666e',
  fondo: '#f3f4f4', blanco: '#ffffff', borde: '#e0e3e6', rojo: '#e05c5c',
};

// producto: { tipo, referencia_id, nombre, marca, modelo, imagen_url }
export default function AgregarAColeccionModal({ producto, onCerrar, onAgregado }) {
  const [colecciones, setColecciones] = useState([]);
  const [seleccion, setSeleccion] = useState(() => new Set());
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);

  // Crear nueva al vuelo
  const [nombreNueva, setNombreNueva] = useState('');
  const [creando, setCreando] = useState(false);

  const cargar = () => {
    setCargando(true);
    fetchAuth(`${API_BASE_CLI}/api/colecciones`)
      .then(r => r.json())
      .then(d => { if (d.ok) setColecciones(d.colecciones || []); })
      .catch(e => { if (e.code !== 'SESION_EXPIRADA') setError('Error al cargar colecciones: ' + (e.message || '')); })
      .finally(() => setCargando(false));
  };

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, []);

  const toggle = (id) => {
    setSeleccion(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const crearNueva = async () => {
    const nombre = nombreNueva.trim();
    if (!nombre) return;
    setCreando(true);
    setError(null);
    try {
      const resp = await fetchAuth(`${API_BASE_CLI}/api/colecciones`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nombre, icono: 'layers', color: '#5c9adb' }),
      });
      const d = await resp.json().catch(() => ({}));
      if (!resp.ok || !d.ok) { setError(d.error || 'No se pudo crear la coleccion.'); setCreando(false); return; }
      setColecciones(prev => [d.coleccion, ...prev]);
      setSeleccion(prev => new Set(prev).add(d.coleccion.id));
      setNombreNueva('');
    } catch (e) {
      if (e.code !== 'SESION_EXPIRADA') setError('Error: ' + (e.message || ''));
    } finally {
      setCreando(false);
    }
  };

  const guardar = async () => {
    if (seleccion.size === 0) { setError('Selecciona al menos una coleccion.'); return; }
    setGuardando(true);
    setError(null);
    const body = JSON.stringify({
      tipo: producto.tipo,
      referencia_id: String(producto.referencia_id),
      nombre: producto.nombre,
      marca: producto.marca || null,
      modelo: producto.modelo || null,
      imagen_url: producto.imagen_url || null,
    });
    try {
      const ids = Array.from(seleccion);
      const resultados = await Promise.all(ids.map(id =>
        fetchAuth(`${API_BASE_CLI}/api/colecciones/${id}/items`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body,
        }).then(r => r.json()).catch(() => ({ ok: false }))
      ));
      const okCount = resultados.filter(r => r && r.ok).length;
      if (okCount === 0) { setError('No se pudo agregar a ninguna coleccion.'); setGuardando(false); return; }
      const nombres = colecciones.filter(c => seleccion.has(c.id)).map(c => c.nombre);
      onAgregado && onAgregado(okCount, nombres);
    } catch (e) {
      if (e.code !== 'SESION_EXPIRADA') setError('Error: ' + (e.message || ''));
      setGuardando(false);
    }
  };

  return (
    <div
      onClick={onCerrar}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(26,58,92,0.55)', zIndex: 1250,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colores.blanco, borderRadius: 14, width: '100%', maxWidth: 440,
          fontFamily: PP, color: colores.gris, padding: 24, maxHeight: 'calc(var(--beep-vh) * 92)', overflowY: 'auto',
        }}
      >
        <div style={{ fontSize: 16, fontWeight: 700, color: colores.oscuro, marginBottom: 4 }}>
          Agregar a coleccion
        </div>
        <div style={{ fontSize: 13, color: colores.gris, marginBottom: 16 }}>
          {producto?.nombre}
        </div>

        {cargando ? (
          <div style={{ color: colores.grisClaro, padding: 12 }}>Cargando colecciones...</div>
        ) : (
          <>
            {colecciones.length === 0 ? (
              <div style={{ padding: 14, background: colores.fondo, borderRadius: 8, fontSize: 13, color: colores.gris, marginBottom: 14 }}>
                No hay colecciones todavia. Crea la primera abajo.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 14, maxHeight: 240, overflowY: 'auto' }}>
                {colecciones.map(c => {
                  const sel = seleccion.has(c.id);
                  const color = c.color || colores.principal;
                  return (
                    <button
                      key={c.id}
                      onClick={() => toggle(c.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left',
                        padding: '9px 11px', borderRadius: 8, cursor: 'pointer', fontFamily: PP,
                        background: sel ? '#e8f0fb' : colores.blanco,
                        border: sel ? `2px solid ${colores.principal}` : `1px solid ${colores.borde}`,
                      }}
                    >
                      <span style={{
                        width: 30, height: 30, borderRadius: 7, flexShrink: 0,
                        background: color + '22', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <IconoLucide nombre={c.icono} size={16} color={color} />
                      </span>
                      <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: colores.gris, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {c.nombre}
                        <span style={{ fontWeight: 400, color: colores.grisClaro }}> · {c.items_total || 0}</span>
                      </span>
                      {sel
                        ? <LucideIcons.CheckCircle2 size={18} color={colores.principal} />
                        : <LucideIcons.Circle size={18} color={colores.borde} />}
                    </button>
                  );
                })}
              </div>
            )}

            {/* Crear nueva al vuelo */}
            <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
              <input
                value={nombreNueva}
                onChange={(e) => setNombreNueva(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') crearNueva(); }}
                placeholder="Nueva coleccion..."
                style={{
                  flex: 1, boxSizing: 'border-box', padding: '9px 11px',
                  border: `1px solid ${colores.borde}`, borderRadius: 8, fontSize: 14,
                  fontFamily: PP, color: colores.gris, outline: 'none',
                }}
              />
              <button
                onClick={crearNueva}
                disabled={creando || !nombreNueva.trim()}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  background: colores.blanco, color: colores.principal,
                  border: `1px solid ${colores.principal}`, borderRadius: 8,
                  padding: '9px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: PP,
                  opacity: (creando || !nombreNueva.trim()) ? 0.6 : 1, whiteSpace: 'nowrap',
                }}
              >
                <LucideIcons.Plus size={15} /> Crear
              </button>
            </div>

            {error && (
              <div style={{ marginBottom: 12, padding: 10, background: '#fdecec', borderRadius: 8, fontSize: 12, color: '#9b2c2c' }}>
                {error}
              </div>
            )}
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button
            onClick={onCerrar}
            disabled={guardando}
            style={{
              background: colores.blanco, color: colores.gris, border: `1px solid ${colores.borde}`,
              padding: '10px 16px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: PP,
            }}
          >Cancelar</button>
          <button
            onClick={guardar}
            disabled={guardando || seleccion.size === 0}
            style={{
              background: colores.principal, color: colores.blanco, border: 'none',
              padding: '10px 18px', borderRadius: 8, fontSize: 14, fontWeight: 600,
              cursor: 'pointer', fontFamily: PP, opacity: (guardando || seleccion.size === 0) ? 0.6 : 1,
            }}
          >{guardando ? 'Agregando...' : 'Agregar'}</button>
        </div>
      </div>
    </div>
  );
}
